import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState } from 'react';

import { type AudienceOptionType, useAudienceOptions } from '@/api/audience';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import type { FilterComponentProps } from '@/providers/TableState';

type Props = FilterComponentProps & {
  mode?: 'multiple';
};

const AudienceFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  ...props
}: Props) => {
  const form = useFilterForm();
  const [search, setSearch] = useState<string>('');
  const { audienceOptions, error, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useAudienceOptions({ search });

  const audienceWithBlanksOption = useMemo<AudienceOptionType[] | undefined>(() => {
    if (!audienceOptions) return undefined;

    const extraOptions: AudienceOptionType[] = [];
    if (search.length === 0) {
      extraOptions.push({ id: '', name: t`(Blanks)` });
    }

    return extraOptions.concat(audienceOptions);
  }, [audienceOptions, search]);

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="audience-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              options={audienceWithBlanksOption}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              placeholder={t`Select Audiences...`}
              loadingPlaceholder={t`Loading Audiences...`}
              notFoundContent={<Trans>No audiences were found...</Trans>}
              onSearch={setSearch}
              loadMore={fetchNextPage}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={audienceWithBlanksOption}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default AudienceFilter;
